"use client";

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils/cn';

interface MatrixTextProps {
  phrases: string[];
  interval?: number;
  className?: string;
  glitchProbability?: number;
}

export function MatrixText({ 
  phrases, 
  interval = 3000,
  className,
  glitchProbability = 0.03
}: MatrixTextProps) {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Matrix character set
  const matrixChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$#@%&*()_+-=[]{}|;:,.<>?";
  
  useEffect(() => {
    // Set initial phrase
    setDisplayText(phrases[currentPhraseIndex]);
    
    // Rotate through phrases
    const rotateInterval = setInterval(() => {
      transitionToNextPhrase();
    }, interval);
    
    return () => clearInterval(rotateInterval);
  }, [currentPhraseIndex, phrases, interval]);
  
  const transitionToNextPhrase = () => {
    if (isTransitioning) return; // Prevent multiple transitions
    
    setIsTransitioning(true);
    
    const nextIndex = (currentPhraseIndex + 1) % phrases.length;
    const targetPhrase = phrases[nextIndex];
    const currentPhrase = phrases[currentPhraseIndex];
    
    // Matrix transition effect
    let iterations = 0;
    const maxIterations = 10; // Number of random character iterations
    let tempText = currentPhrase;
    
    const matrixInterval = setInterval(() => {
      if (iterations < maxIterations) {
        // Generate a new string with some characters replaced with random matrix characters
        tempText = tempText.split('').map((char, i) => {
          // During transition, randomly replace characters with matrix characters
          if (Math.random() < glitchProbability * 5) {
            return matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
          }
          return char;
        }).join('');
        
        setDisplayText(tempText);
        iterations++;
      } else {
        // Final transition to the target phrase
        clearInterval(matrixInterval);
        setCurrentPhraseIndex(nextIndex);
        setDisplayText(targetPhrase);
        setIsTransitioning(false);
      }
    }, 50);
  };
  
  // Random glitch effect during normal display
  useEffect(() => {
    if (!isTransitioning) {
      const glitchInterval = setInterval(() => {
        const currentPhrase = phrases[currentPhraseIndex];
        
        // Occasionally glitch a character
        if (Math.random() < glitchProbability) {
          const glitchedText = currentPhrase.split('').map((char, i) => {
            if (Math.random() < glitchProbability) {
              return matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
            }
            return char;
          }).join('');
          
          setDisplayText(glitchedText);
          
          // Reset back to original after a short delay
          setTimeout(() => {
            if (!isTransitioning) {
              setDisplayText(currentPhrase);
            }
          }, 100);
        }
      }, 200);
      
      return () => clearInterval(glitchInterval);
    }
  }, [currentPhraseIndex, isTransitioning, phrases, glitchProbability]);
  
  return (
    <div className={cn("font-mono inline-block", className)}>
      {displayText}
    </div>
  );
}